import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import { aiExtractLatestText, aiSendQualificacaoMessage, aiWaitForQualificacaoResult } from './ai_helper'

/**
 * Desenha um retângulo com bordas arredondadas
 */
function drawRoundedRect(page, x, y, width, height, radius, borderColor, borderWidth = 2) {
  // Desenhar as linhas retas
  // Linha superior
  page.drawLine({
    start: { x: x + radius, y: y + height },
    end: { x: x + width - radius, y: y + height },
    thickness: borderWidth,
    color: borderColor,
  })
  
  // Linha direita
  page.drawLine({
    start: { x: x + width, y: y + height - radius },
    end: { x: x + width, y: y + radius },
    thickness: borderWidth,
    color: borderColor,
  })
  
  // Linha inferior
  page.drawLine({
    start: { x: x + width - radius, y: y },
    end: { x: x + radius, y: y },
    thickness: borderWidth,
    color: borderColor,
  })
  
  // Linha esquerda
  page.drawLine({
    start: { x: x, y: y + radius },
    end: { x: x, y: y + height - radius },
    thickness: borderWidth,
    color: borderColor,
  })
  
  // Desenhar os cantos arredondados usando aproximação com linhas
  const steps = 60
  
  // Canto superior esquerdo (arco de 180° a 270°)
  for (let i = 0; i <= steps; i++) {
    const angle1 = Math.PI + (Math.PI / 2) * (i / steps)
    const angle2 = Math.PI + (Math.PI / 2) * ((i + 1) / steps)
    if (i < steps) {
      page.drawLine({
        start: {
          x: x + radius + radius * Math.cos(angle1),
          y: y + height - radius - radius * Math.sin(angle1),
        },
        end: {
          x: x + radius + radius * Math.cos(angle2),
          y: y + height - radius - radius * Math.sin(angle2),
        },
        thickness: borderWidth,
        color: borderColor,
      })
    }
  }
  
  // Canto superior direito (arco de 270° a 360°)
  for (let i = 0; i <= steps; i++) {
    const angle1 = (Math.PI / 2) - (Math.PI / 2) * (i / steps)
    const angle2 = (Math.PI / 2) - (Math.PI / 2) * ((i + 1) / steps)
    if (i < steps) {
      page.drawLine({
        start: {
          x: x + width - radius + radius * Math.cos(angle1),
          y: y + height - radius + radius * Math.sin(angle1),
        },
        end: {
          x: x + width - radius + radius * Math.cos(angle2),
          y: y + height - radius + radius * Math.sin(angle2),
        },
        thickness: borderWidth,
        color: borderColor,
      })
    }
  }
  
  // Canto inferior direito (arco de 0° a 90°)
  for (let i = 0; i <= steps; i++) {
    const angle1 = -(Math.PI / 2) * (i / steps)
    const angle2 = -(Math.PI / 2) * ((i + 1) / steps)
    if (i < steps) {
      page.drawLine({
        start: {
          x: x + width - radius + radius * Math.cos(angle1),
          y: y + radius + radius * Math.sin(angle1),
        },
        end: {
          x: x + width - radius + radius * Math.cos(angle2),
          y: y + radius + radius * Math.sin(angle2),
        },
        thickness: borderWidth,
        color: borderColor,
      })
    }
  }
  
  // Canto inferior esquerdo (arco de 90° a 180°)
  for (let i = 0; i <= steps; i++) {
    const angle1 = (Math.PI / 2) + (Math.PI / 2) * (i / steps)
    const angle2 = (Math.PI / 2) + (Math.PI / 2) * ((i + 1) / steps)
    if (i < steps) {
      page.drawLine({
        start: {
          x: x + radius + radius * Math.cos(angle1),
          y: y + radius - radius * Math.sin(angle1),
        },
        end: {
          x: x + radius + radius * Math.cos(angle2),
          y: y + radius - radius * Math.sin(angle2),
        },
        thickness: borderWidth,
        color: borderColor,
      })
    }
  }
}

/**
 * Desenha um retângulo preenchido com bordas arredondadas
 */
function drawFilledRoundedRect(page, x, y, width, height, radius, fillColor) {
  // Desenhar retângulo principal (sem arredondar)
  page.drawRectangle({
    x: x,
    y: y,
    width: width,
    height: height,
    color: fillColor,
  })
  
  // Desenhar círculos grandes preenchidos nos 4 cantos para criar o efeito arredondado
  // Canto superior esquerdo
  page.drawCircle({
    x: x + radius,
    y: y + height - radius,
    size: radius,
    color: fillColor,
    borderWidth: 0,
  })
  
  // Canto superior direito
  page.drawCircle({
    x: x + width - radius,
    y: y + height - radius,
    size: radius,
    color: fillColor,
    borderWidth: 0,
  })
  
  // Canto inferior esquerdo
  page.drawCircle({
    x: x + radius,
    y: y + radius,
    size: radius,
    color: fillColor,
    borderWidth: 0,
  })
  
  // Canto inferior direito
  page.drawCircle({
    x: x + width - radius,
    y: y + radius,
    size: radius,
    color: fillColor,
    borderWidth: 0,
  })
}

const AI_GUEST_ID_KEY = 'AI_GUEST_USER_ID'

function resolveAiUserId() {
  try {
    const raw = localStorage.getItem('authUser') || window.sessionStorage.getItem('authUser')
    if (raw) {
      const obj = JSON.parse(raw)
      const idCandidate = obj?.id ?? obj?.uid ?? obj?.userId ?? obj?.user_id ?? ''
      if (idCandidate) return String(idCandidate)
    }
  } catch (_) {}

  try {
    let guestId = window.sessionStorage.getItem(AI_GUEST_ID_KEY)
    if (!guestId) {
      const uuid = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
      guestId = `guest-${uuid}`
      try { window.sessionStorage.setItem(AI_GUEST_ID_KEY, guestId) } catch (_) {}
    }
    return guestId
  } catch (_) {
    return ''
  }
}

function truncateForPrompt(text, maxLength = 400) {
  if (!text) return ''
  const normalized = String(text).replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength - 3)}...`
}

function buildAiPrompt(processo, publicacao) {
  if (!processo) return ''

  const parts = []
  if (processo.NumeroCNJ) parts.push(`Número CNJ: ${processo.NumeroCNJ}.`)
  if (processo.Situacao) parts.push(`Situação atual: ${processo.Situacao}.`)
  if (processo.ClassificacaoProcesso) parts.push(`Classificação: ${processo.ClassificacaoProcesso}.`)
  if (processo.Observacao) parts.push(`Observações: ${truncateForPrompt(processo.Observacao, 200)}.`)

  const lead = processo.Lead || {}
  if (lead.Reclamante) parts.push(`Reclamante: ${lead.Reclamante}.`)
  if (lead.ReuRazaoSocial) parts.push(`Réu: ${lead.ReuRazaoSocial}.`)
  if (lead.ValorCausa) parts.push(`Valor da causa: ${lead.ValorCausa}.`)
  if (lead.Tribunal) parts.push(`Tribunal: ${lead.Tribunal}.`)
  if (lead.Vara) parts.push(`Vara: ${lead.Vara}.`)
  if (lead.Esfera) parts.push(`Esfera: ${lead.Esfera}.`)
  if (lead.ObjetoSegurado) parts.push(`Objeto segurado: ${lead.ObjetoSegurado}.`)

  if (publicacao) {
    if (publicacao.Caderno) parts.push(`Caderno: ${publicacao.Caderno}.`)
    if (publicacao.OrgaoPublicador) parts.push(`Órgão publicador: ${publicacao.OrgaoPublicador}.`)
    if (publicacao.Resumo) parts.push(`Resumo existente: ${truncateForPrompt(publicacao.Resumo, 300)}.`)
    if (publicacao.Descricao) parts.push(`Descrição da publicação: ${truncateForPrompt(publicacao.Descricao, 300)}.`)
    if (publicacao.Complemento) parts.push(`Complemento: ${truncateForPrompt(publicacao.Complemento, 200)}.`)
  }

  const context = parts.join(' ')

  return `Com base nos dados do processo id ${processo.Id || ''}, gere uma resposta em JSON com as chaves "nova_emissao" e "objeto_acao". ` +
    'Use linguagem jurídica objetiva em português. ' +
    '"nova_emissao" deve explicar, em até 2 parágrafos curtos, a oportunidade de nova emissão de garantia ou próximos passos financeiros relevantes. ' +
    '"objeto_acao" deve resumir, em até 3 parágrafos curtos, o objeto principal da ação judicial, fundamento legal e status atual. ' +
    'Não inclua texto fora do JSON. ' +
    (context ? `Dados úteis: ${context}` : '')
}

function safeParseAiJson(text) {
  if (!text) return null
  let payload = String(text).trim()

  if (!payload) return null

  if (payload.startsWith('```')) {
    const lines = payload.split('```')
    if (lines.length >= 3) {
      payload = lines[1]
    } else {
      payload = payload.replace(/^```(?:json)?/i, '').replace(/```$/i, '').trim()
    }
  }

  const jsonMatch = payload.match(/\{[\s\S]*\}/)
  const candidate = jsonMatch ? jsonMatch[0] : payload

  try {
    return JSON.parse(candidate)
  } catch (_) {
    return null
  }
}

async function fetchAiNovaEmissaoEObjeto(processo, publicacao) {
  if (!processo?.Id) return null

  const userId = resolveAiUserId()
  if (!userId) return null

  const prompt = buildAiPrompt(processo, publicacao)
  if (!prompt) return null

  try {
    const postResp = await aiSendQualificacaoMessage({
      message: prompt,
      userId,
      idProcesso: processo.Id,
      conversationId: ''
    })

    const jobId = postResp?.id || ''
    if (!jobId) return null

    const final = await aiWaitForQualificacaoResult(jobId, {
      userId,
      pollIntervalMs: 800,
      maxWaitMs: 120000
    })

    const answer = aiExtractLatestText(final)
    const parsed = safeParseAiJson(answer)
    if (!parsed) return null

    return {
      novaEmissao: parsed.nova_emissao || parsed.novaEmissao || '',
      objetoAcao: parsed.objeto_acao || parsed.objetoAcao || ''
    }
  } catch (error) {
    console.error('Erro ao consultar IA para geração do PDF:', error)
    return null
  }
}

/**
 * Carrega uma imagem como array buffer
 */
async function loadImageAsArrayBuffer(imagePath) {
  try {
    const response = await fetch(imagePath)
    if (!response.ok) throw new Error(`Falha ao carregar imagem: ${imagePath}`)
    return await response.arrayBuffer()
  } catch (error) {
    console.error('Erro ao carregar imagem:', error)
    return null
  }
}

/**
 * Gera um PDF com as informações do processo judicial
 * @param {Object} processo - Dados do processo
 * @param {Object} publicacao - Dados da publicação (opcional, primeira será usada se não fornecida)
 * @param {string} destino - Destino do lead (pottencial, xp, demais)
 * @returns {Promise<Blob>} - PDF como Blob
 */
export async function generateProcessoPDF(processo, publicacao = null, destino = 'pottencial') {
  try {
    // Criar um novo documento PDF
    const pdfDoc = await PDFDocument.create()
    
    // Adicionar uma página
    const page = pdfDoc.addPage([595.28, 841.89]) // A4
    const { width, height } = page.getSize()
    
    // Carregar fontes
    const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica)
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
    
    // Carregar logos baseado no destino
    let logoImage = null
    let logoWidth = 0
    let logoHeight = 0
    
    if (destino === 'xp') {
      try {
        // Carregar logo XP
        const logoPath = require('../assets/images/pdf/LOGO XP.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          // Ajustar para ter aproximadamente 100px de largura
          const originalDims = logoImage.scale(1)
          const targetWidth = 100
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo XP:', error)
      }
    } else if (destino === 'pottencial') {
      try {
        // Carregar logo Pottencial
        const logoPath = require('../assets/images/pdf/LOGO-POTTENCIAL.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          const logoDims = logoImage.scale(0.25) // Escala ajustada para a logo Pottencial
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Pottencial:', error)
      }
    } else if (destino === 'daycoval') {
      try {
        // Carregar logo Daycoval
        const logoPath = require('../assets/images/pdf/Daycoval-logo.jpg')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedJpg(logoBytes)
          // Ajustar para ter aproximadamente 100px de largura
          const originalDims = logoImage.scale(1)
          const targetWidth = 150
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Daycoval:', error)
      }
    } else if (destino === 'abc') {
      try {
        // Carregar logo ABC
        const logoPath = require('../assets/images/pdf/LOGO-ABC.jpeg')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedJpg(logoBytes)
          // Ajustar para ter aproximadamente 45px de largura (reduzido em 70%)
          const originalDims = logoImage.scale(1)
          const targetWidth = 45
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo ABC:', error)
      }
    } else if (destino === 'galapagos') {
      try {
        // Carregar logo Galapagos
        const logoPath = require('../assets/images/pdf/logo-GALAPAGOS.jpeg')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedJpg(logoBytes)
          // Ajustar para ter aproximadamente 150px de largura
          const originalDims = logoImage.scale(1)
          const targetWidth = 150
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Galapagos:', error)
      }
    } else if (destino === 'genial') {
      try {
        const logoPath = require('../assets/images/pdf/GENIAL.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 150
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Genial:', error)
      }
    } else if (destino === 'safra') {
      try {
        const logoPath = require('../assets/images/pdf/SAFRA.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 150
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Safra:', error)
      }
    } else if (destino === 'ezze') {
      try {
        const logoPath = require('../assets/images/pdf/ezze-seguros-logo.jpg')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedJpg(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 120
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Ezze:', error)
      }
    } else if (destino === 'lockton') {
      try {
        const logoPath = require('../assets/images/pdf/lockton.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 100
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Lockton:', error)
      }
    } else if (destino === 'swissre') {
      try {
        const logoPath = require('../assets/images/pdf/swiss-re.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 150
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Swiss Re:', error)
      }
    } else if (destino === 'aon') {
      try {
        const logoPath = require('../assets/images/pdf/aon.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 120
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Aon:', error)
      }
    } else if (destino === 'avla') {
      try {
        const logoPath = require('../assets/images/pdf/avla.jpg')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedJpg(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 120
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Avla:', error)
      }
    } else if (destino === 'tokio') {
      try {
        const logoPath = require('../assets/images/pdf/tokio.png')
        const logoBytes = await loadImageAsArrayBuffer(logoPath)
        if (logoBytes) {
          logoImage = await pdfDoc.embedPng(logoBytes)
          const originalDims = logoImage.scale(1)
          const targetWidth = 120
          const scale = targetWidth / originalDims.width
          const logoDims = logoImage.scale(scale)
          logoWidth = logoDims.width
          logoHeight = logoDims.height
        }
      } catch (error) {
        console.error('Erro ao carregar logo Tokio Marine:', error)
      }
    }
    
    // Carregar ícone da balança
    let lawIcon = null
    try {
      const lawIconPath = destino === 'xp'
        ? require('../assets/images/pdf/lawxp.png')
        : destino === 'daycoval'
        ? require('../assets/images/pdf/layday.png')
        : destino === 'abc'
        ? require('../assets/images/pdf/layABC.png')
        : destino === 'galapagos'
        ? require('../assets/images/pdf/lawGalapagos.png')
        : destino === 'genial'
        ? require('../assets/images/pdf/lawGenial.png')
        : destino === 'safra'
        ? require('../assets/images/pdf/lawSafra.png')
        : destino === 'ezze'
        ? require('../assets/images/pdf/lawEzze.png')
        : destino === 'lockton'
        ? require('../assets/images/pdf/lowLockton.png')
        : destino === 'swissre'
        ? require('../assets/images/pdf/lowSwiss.png')
        : destino === 'aon'
        ? require('../assets/images/pdf/lowAon.png')
        : destino === 'avla'
        ? require('../assets/images/pdf/lowAvla.png')
        : destino === 'tokio'
        ? require('../assets/images/pdf/lowTokio.png')
        : require('../assets/images/pdf/law.png')
      const lawIconBytes = await loadImageAsArrayBuffer(lawIconPath)
      if (lawIconBytes) {
        lawIcon = await pdfDoc.embedPng(lawIconBytes)
      }
    } catch (error) {
      console.error('Erro ao carregar ícone da balança:', error)
    }
    
    // Carregar ícone de documento
    let paperIcon = null
    try {
      const paperIconPath = destino === 'xp'
        ? require('../assets/images/pdf/paperxp.png')
        : destino === 'daycoval'
        ? require('../assets/images/pdf/paperday.png')
        : destino === 'abc'
        ? require('../assets/images/pdf/paperABC.png')
        : destino === 'galapagos'
        ? require('../assets/images/pdf/paperGalapagos.png')
        : destino === 'genial'
        ? require('../assets/images/pdf/paperGenial.png')
        : destino === 'safra'
        ? require('../assets/images/pdf/paperSafra.png')
        : destino === 'ezze'
        ? require('../assets/images/pdf/paperEzze.png')
        : destino === 'lockton'
        ? require('../assets/images/pdf/paperLockton.png')
        : destino === 'swissre'
        ? require('../assets/images/pdf/paperSwiss.png')
        : destino === 'aon'
        ? require('../assets/images/pdf/paperAon.png')
        : destino === 'avla'
        ? require('../assets/images/pdf/paperAvla.png')
        : destino === 'tokio'
        ? require('../assets/images/pdf/paperTokio.png')
        : require('../assets/images/pdf/paper.png')
      const paperIconBytes = await loadImageAsArrayBuffer(paperIconPath)
      if (paperIconBytes) {
        paperIcon = await pdfDoc.embedPng(paperIconBytes)
      }
    } catch (error) {
      console.error('Erro ao carregar ícone de documento:', error)
    }
    
    // Carregar ícone de documento alternativo (para Apólice)
    let paperAltIcon = null
    try {
      const paperAltIconPath = destino === 'xp'
        ? require('../assets/images/pdf/paperAltxp.png')
        : destino === 'daycoval'
        ? require('../assets/images/pdf/paperAltday.png')
        : destino === 'abc'
        ? require('../assets/images/pdf/paperAltABC.png')
        : destino === 'galapagos'
        ? require('../assets/images/pdf/paperAltGalapagos.png')
        : destino === 'genial'
        ? require('../assets/images/pdf/paperAltGenial.png')
        : destino === 'safra'
        ? require('../assets/images/pdf/paperAltSafra.png')
        : destino === 'ezze'
        ? require('../assets/images/pdf/paperAltEzze.png')
        : destino === 'lockton'
        ? require('../assets/images/pdf/paperAltLockton.png')
        : destino === 'swissre'
        ? require('../assets/images/pdf/paperAltSwiss.png')
        : destino === 'aon'
        ? require('../assets/images/pdf/papelAltAon.png')
        : destino === 'avla'
        ? require('../assets/images/pdf/paperAltAvla.png')
        : destino === 'tokio'
        ? require('../assets/images/pdf/paperAltTokio.png')
        : require('../assets/images/pdf/paperAlt.png')
      const paperAltIconBytes = await loadImageAsArrayBuffer(paperAltIconPath)
      if (paperAltIconBytes) {
        paperAltIcon = await pdfDoc.embedPng(paperAltIconBytes)
      }
    } catch (error) {
      console.error('Erro ao carregar ícone de documento alternativo:', error)
    }
    
    // Cores
    const primaryColor = rgb(0.71, 0.53, 0.29) // #B68749 (dourado/marrom)
    const textColor = rgb(0.2, 0.2, 0.2)
    const lightGray = rgb(0.95, 0.95, 0.95)
    const borderColor = rgb(0.8, 0.8, 0.8)
    const blackBorder = rgb(0, 0, 0) // Borda preta
    
    // Cor do header/footer baseada no destino
    let headerFooterColor
    if (destino === 'pottencial') {
      headerFooterColor = rgb(0.949, 0.396, 0.133) // #F26522 (laranja Pottencial)
    } else if (destino === 'xp') {
      headerFooterColor = rgb(1, 0.776, 0.035) // #ffc609 (amarelo XP)
    } else if (destino === 'daycoval') {
      headerFooterColor = rgb(0.392, 0.592, 0.686) // #6497af (azul Daycoval)
    } else if (destino === 'abc') {
      headerFooterColor = rgb(0.651, 0.529, 0.294) // #a6874b (cor primária ABC)
    } else if (destino === 'galapagos') {
      headerFooterColor = rgb(0.094, 0.235, 0.502) // #183c80 (cor primária Galapagos)
    } else if (destino === 'genial') {
      headerFooterColor = rgb(0.004, 0.055, 0.58) // #010e94
    } else if (destino === 'safra') {
      headerFooterColor = rgb(0, 0, 0.227) // #00003a
    } else if (destino === 'ezze') {
      headerFooterColor = rgb(0.176, 0.773, 0.180) // #2DC52E
    } else if (destino === 'lockton') {
      headerFooterColor = rgb(0.725, 0.725, 0.725) // #B9B9B9
    } else if (destino === 'swissre') {
      headerFooterColor = rgb(0.384, 0.490, 0.467) // #627D77
    } else if (destino === 'aon') {
      headerFooterColor = rgb(0.918, 0, 0.039) // #EA000A
    } else if (destino === 'avla') {
      headerFooterColor = rgb(0.004, 0.443, 0.808) // #0171CE
    } else if (destino === 'tokio') {
      headerFooterColor = rgb(0.071, 0.314, 0.106) // #12501B
    } else {
      headerFooterColor = rgb(0, 0, 0) // Preto para outros destinos
    }

    const subtitleColor = destino === 'avla'
      ? rgb(0.106, 0.400, 0.776) // #1B66C6
      : destino === 'tokio'
      ? rgb(0.165, 0.404, 0.427) // #2A676D
      : headerFooterColor
    
    // Definir margens do documento
    const documentMargin = 20
    
    // Header (fora do container)
    const headerHeight = 100 // Altura do header aumentada para acomodar mais conteúdo
    
    // Fundo branco para o header
    page.drawRectangle({
      x: 0,
      y: height - headerHeight,
      width: width,
      height: headerHeight,
      color: rgb(1, 1, 1), // Fundo branco
    })
    
    // Número do processo no topo esquerdo
    const processoText = sanitizeTextForPDF(`Processo:   ${processo.NumeroCNJ || '5000947-35.2019.4.03.6126'}`)
    page.drawText(processoText, {
      x: 30,
      y: height - 25,
      size: 11,
      font: fontRegular,
      color: rgb(0, 0, 0),
    })
    
    // Desenhar logo no header no canto superior direito (se disponível)
    if (logoImage) {
      const logoX = width - logoWidth - 30
      const logoY = height - headerHeight + (headerHeight - logoHeight) / 2
      page.drawImage(logoImage, {
        x: logoX,
        y: logoY,
        width: logoWidth,
        height: logoHeight,
      })
    }
    
    // Título "Insight Report" grande e em negrito
    const titleText = 'Insight Report'
    const titleSize = 28
    page.drawText(titleText, {
      x: 30,
      y: height - 55,
      size: titleSize,
      font: fontBold,
      color: rgb(0, 0, 0),
    })
    
    // Subtítulo "Oportunidade Garantia Judicial" em laranja
    const subtitleText = 'Oportunidade Garantia Judicial'
    const subtitleSize = 14
    page.drawText(subtitleText, {
      x: 30,
      y: height - 80,
      size: subtitleSize,
      font: fontRegular,
      color: subtitleColor,
    })
    
    // Footer (fora do container)
    const footerHeight = 50 // Altura do footer em pixels
    page.drawRectangle({
      x: 0,
      y: 0, // Posiciona na parte inferior da página
      width: width,
      height: footerHeight,
      color: headerFooterColor,
    })
    
    // Ajustar yPosition para começar abaixo do header
    let yPosition = height - headerHeight - 5
    
    // Definir posição inicial do container (seção de informações)
    const containerStartY = yPosition
    const containerPaddingY = 20 // Padding vertical (top/bottom)
    const containerPaddingX = 15 // Padding horizontal (left/right)
    const containerX = documentMargin
    const containerWidth = width - (documentMargin * 2)
    const borderRadius = 10
    
    // Ajustar posições para dentro do container de informações
    yPosition -= containerPaddingY
    
    // Selecionar publicação para usar em todo o documento
    const selectedPublicacao = publicacao || (Array.isArray(processo?.Publicacoes) && processo.Publicacoes.length > 0 ? processo.Publicacoes[0] : null)
    
    // Função auxiliar para desenhar campo
    const drawField = (label, value, x, y, maxWidth = 180) => {
      const labelText = label + ': '
      const labelWidth = fontBold.widthOfTextAtSize(labelText, 9)
      
      // Desenhar label (negrito)
      page.drawText(labelText, {
        x,
        y,
        size: 9,
        font: fontBold,
        color: textColor,
      })
      
      // Desenhar valor ao lado do label
      let valueText = value ? sanitizeTextForPDF(String(value)) : '-'
      const availableWidth = maxWidth - labelWidth
      let valueWidth = fontRegular.widthOfTextAtSize(valueText, 9)
      
      // Truncar o texto se ultrapassar o espaço disponível
      if (valueWidth > availableWidth) {
        while (valueWidth > availableWidth - fontRegular.widthOfTextAtSize('...', 9) && valueText.length > 0) {
          valueText = valueText.slice(0, -1)
          valueWidth = fontRegular.widthOfTextAtSize(valueText, 9)
        }
        valueText += '...'
      }
      
      page.drawText(valueText, {
        x: x + labelWidth,
        y: y,
        size: 9,
        font: fontRegular,
        color: textColor,
      })
    }
    
    // Seção de informações principais em grid (2 colunas)
    const col1X = containerX + containerPaddingX
    const col2X = containerX + containerPaddingX + 285 // Segunda coluna mais à direita
    
    // Data Situação vem da publicação selecionada
    let dataSituacao = '-'
    if (selectedPublicacao?.DataPublicacao) {
      try {
        const dataPubli = new Date(selectedPublicacao.DataPublicacao)
        dataSituacao = dataPubli.toLocaleDateString('pt-BR')
      } catch (_) {}
    }
    
    // Linha 1
    drawField('Situação', processo.Lead?.SituacaoLead || 'Suspenso / Sobrestado', col1X, yPosition, 250)
    drawField('Data Situação', dataSituacao, col2X, yPosition, 280)
    
    yPosition -= 20
    
    // Linha 2
    drawField('Tipo', processo.Lead?.TipoLead || 'Judicial', col1X, yPosition, 250)
    drawField('Objeto', processo.Lead?.ObjetoSegurado || 'Garantia do Juízo', col2X, yPosition, 280)
    
    yPosition -= 20
    
    // Linha 3
    drawField('Esfera', processo.Lead?.Esfera || 'Cível/Fiscal', col1X, yPosition, 250)
    drawField('Valor da Causa', 
      processo.Lead?.ValorCausa ? `R$ ${parseFloat(processo.Lead.ValorCausa).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-', 
      col2X, yPosition, 280)
    
    yPosition -= 20
    
    // Linha 4
    drawField('Tribunal', processo.Lead?.Tribunal || '-', col1X, yPosition, 250)
    drawField('Classificação Processo', 
      processo.ClassificacaoProcesso || 'Execução Fiscal', 
      col2X, yPosition, 280)
    
    yPosition -= 20
    
    // Linha 5
    drawField('Cliente', processo.Lead?.ReuRazaoSocial || 'CVC BRASIL OPERADORA E AGENCIA DE VIAGENS S.A', col1X, yPosition, 250)
    drawField('Parte Contrária', 
      processo.Lead?.Reclamante || 'UNIAO FEDERAL', 
      col2X, yPosition, 280)
    
    yPosition -= 20
    
    // Linha 6
    drawField('Processos Relacionados', 
      processo.Lead?.ProcessoRelacionado || '-', 
      col1X, yPosition, 250)
    
    // Calcular a posição final do container (após última linha + padding)
    const containerEndY = yPosition - 10
    const containerHeight = containerStartY - containerEndY
    
    // Espaçamento de 10px entre cards
    yPosition = containerEndY - 10
    
    // Buscar texto do Resumo (Objeto da ação)
    let objetoAcaoText = ''
    
    // Verificar se Observações tem mais de 10 palavras ou length > 10
    const observacoes = processo?.Observacao ? String(processo.Observacao).trim() : ''
    const observacoesPalavras = observacoes ? observacoes.split(/\s+/).filter(p => p.length > 0).length : 0
    const observacoesLength = observacoes.length
    
    // Se Observações tiver mais de 10 palavras OU length > 10, usar Observações como resumo
    if (observacoes && (observacoesPalavras > 10 || observacoesLength > 10)) {
      objetoAcaoText = observacoes
    } else {
      // Caso contrário, seguir lógica normal: primeiro verificar Resumo da publicação
      objetoAcaoText = selectedPublicacao?.Resumo ? String(selectedPublicacao.Resumo).trim() : ''
      
      // Se não tiver Resumo na publicação, usar IA para gerar
      const precisaAi = selectedPublicacao && !objetoAcaoText
      if (precisaAi) {
        const aiTexts = await fetchAiNovaEmissaoEObjeto(processo, selectedPublicacao)
        if (!objetoAcaoText && aiTexts?.objetoAcao) objetoAcaoText = String(aiTexts.objetoAcao).trim()
      }
    }

    const fallbackObjetoAcao = 'Trata da suspensão da exigibilidade de débitos não tributários relacionados ao ressarcimento ao SUS, envolvendo a operadora de saúde e o valor total de R$ 24.694.491,09. Argumenta que a garantia será fornecida por meio de seguro garantia, amparada pelo Código Tributário Nacional (art. 151, inc. II) e jurisprudência associada. Ele também aborda a Resolução Normativa ANS nº 502/22, que regulamenta como o cálculo de ressarcimento ao SUS deve ocorrer, aplicando o Índice de Valoração do Ressarcimento (IVR) de 1,5 vezes os valores da tabela SUS. No entanto, o texto critica a falta de auditoria e consistência nos dados administrativos apresentados. A empresa cita que irá realizar apresentação de seguro garantia, posteriormente do despacho de autorização do juiz. Destarte, vale mencionar, que foi dado o despacho inicial apenas para emenda da inicial, sem mencionar a autorização do seguro garantia.'

    objetoAcaoText = objetoAcaoText || fallbackObjetoAcao

    // Seção Oportunidade (seguindo o mesmo padrão do card acima)
    if (selectedPublicacao) {
      
      const oportunidadeStartY = yPosition
      
      // Background do título "Oportunidade" - cor baseada no destino
      const oportunidadeBgColor = destino === 'xp'
        ? rgb(1, 0.957, 0.847)    // #fff4d8 (amarelo claro XP)
        : destino === 'daycoval'
        ? rgb(0.816, 0.878, 0.906) // #d0e0e7 (azul claro Daycoval)
        : destino === 'abc'
        ? rgb(0.894, 0.859, 0.788) // #e4dbc9 (cor secundária ABC)
        : destino === 'galapagos'
        ? rgb(0.729, 0.769, 0.851) // #bac4d9 (cor secundária Galapagos)
        : destino === 'genial'
        ? rgb(0.706, 0.729, 0.871) // #b4bade
        : destino === 'safra'
        ? rgb(0.706, 0.702, 0.765) // #b4b3c3
        : destino === 'ezze'
        ? rgb(0.835, 0.957, 0.836) // verde claro Ezze (#2DC52E)
        : destino === 'lockton'
        ? rgb(0.725, 0.725, 0.725) // #B9B9B9
        : destino === 'swissre'
        ? rgb(0.816, 0.847, 0.839) // #D0D8D6
        : destino === 'aon'
        ? rgb(0.973, 0.702, 0.714) // #F8B3B6
        : destino === 'avla'
        ? rgb(0.702, 0.831, 0.941) // #B3D4F0
        : destino === 'tokio'
        ? rgb(0.722, 0.792, 0.733) // #B8CABB
        : rgb(0.984, 0.820, 0.741) // #fbd1bd (laranja claro Pottencial)
      const oportunidadeBoxHeight = 50
      const oportunidadeBoxY = yPosition - oportunidadeBoxHeight
      
      // Desenhar retângulo de fundo
      page.drawRectangle({
        x: containerX,
        y: oportunidadeBoxY,
        width: containerWidth,
        height: oportunidadeBoxHeight,
        color: oportunidadeBgColor,
      })
      
      // Desenhar bordas arredondadas com a mesma cor do fundo para criar o efeito
      drawRoundedRect(
        page,
        containerX,
        oportunidadeBoxY,
        containerWidth,
        oportunidadeBoxHeight,
        8, // Raio de 8px
        oportunidadeBgColor,
        6 // Espessura maior da borda para cobrir os cantos completamente
      )
      
      // Ícone da balança (sem quadrado de fundo)
      const iconSize = 30
      const iconX = containerX + 15
      const iconY = oportunidadeBoxY + (oportunidadeBoxHeight - iconSize) / 2
      
      // Desenhar apenas o ícone da balança
      if (lawIcon) {
        page.drawImage(lawIcon, {
          x: iconX,
          y: iconY,
          width: iconSize + 5,
          height: iconSize,
        })
      }
      
      // Texto "Oportunidade:" e valor
      const textX = iconX + iconSize + 15
      const textY = oportunidadeBoxY + (oportunidadeBoxHeight / 2) - 3
      
      page.drawText('Oportunidade:', {
        x: textX,
        y: textY,
        size: 12,
        font: fontBold,
        color: rgb(0, 0, 0),
      })
      
      // Valor da oportunidade: Se tiver apólice é "Renovação", senão "Nova Emissão"
      const temApolice = Array.isArray(processo?.Apolices) && processo.Apolices.length > 0
      const oportunidadeValor = temApolice ? 'Renovação' : 'Nova Emissão'
      const oportunidadeLabelWidth = fontBold.widthOfTextAtSize('Oportunidade:', 12)
      
      page.drawText(oportunidadeValor, {
        x: textX + oportunidadeLabelWidth + 10,
        y: textY,
        size: 12,
        font: fontRegular,
        color: rgb(0, 0, 0),
      })
      
      yPosition = oportunidadeBoxY - 10
      
      // Calcular posição final do container de Oportunidade
      const oportunidadeEndY = yPosition
      const oportunidadeHeight = oportunidadeStartY - oportunidadeEndY
      
      // Espaçamento de 10px para o próximo card
      yPosition = oportunidadeEndY - 10
    }
    
    // Seção Resumo (seguindo o mesmo padrão do card acima)
    if (selectedPublicacao) {
      
      const resumoStartY = yPosition
      const resumoFixedHeight = 300 // Altura fixa do card de Resumo
      const resumoEndY = resumoStartY - resumoFixedHeight
      
      // Desenhar o container com bordas arredondadas primeiro (usa cor principal)
      drawRoundedRect(page, containerX, resumoEndY, containerWidth, resumoFixedHeight, borderRadius, headerFooterColor, 1)
      
      // Título "Resumo" - sem background, apenas ícone e texto
      const resumoIconBoxSize = 30
      const resumoIconBoxX = containerX + containerPaddingX
      const resumoIconBoxY = yPosition - resumoIconBoxSize - 10
      
      // Ícone de documento (sem quadrado de fundo)
      if (paperIcon) {
        page.drawImage(paperIcon, {
          x: resumoIconBoxX,
          y: resumoIconBoxY,
          width: resumoIconBoxSize + 5,
          height: resumoIconBoxSize,
        })
      }
      
      // Texto "Resumo:"
      const resumoTextX = resumoIconBoxX + resumoIconBoxSize + 15
      const resumoTextY = resumoIconBoxY + (resumoIconBoxSize / 2) - 5
      
      page.drawText('Resumo:', {
        x: resumoTextX,
        y: resumoTextY,
        size: 12,
        font: fontBold,
        color: rgb(0, 0, 0),
      })
      
      yPosition = resumoIconBoxY - 20 // 10px adicionais de margin bottom
      
      const maxTextWidth = containerWidth - containerPaddingX * 2 // Largura máxima em pixels
      const resumoLines = wrapText(objetoAcaoText, maxTextWidth, fontRegular, 9)
      
      // Espaçamento fixo menor entre linhas
      const lineSpacing = 12
      
      for (const line of resumoLines) {
        page.drawText(line, {
          x: containerX + containerPaddingX,
          y: yPosition,
          size: 9,
          font: fontRegular,
          color: textColor,
        })
        yPosition -= lineSpacing
      }
      
      // Atualizar yPosition para após o card
      yPosition = resumoEndY
    }
    
    // Espaçamento de 10px para o próximo card
    yPosition -= 10
    
    // Título "Apólice no Processo" - fora do retângulo, seguindo o mesmo estilo de "Oportunidade"
    const apolicesBgColor = destino === 'xp'
      ? rgb(1, 0.957, 0.847)    // #fff4d8 (amarelo claro XP)
      : destino === 'daycoval'
      ? rgb(0.816, 0.878, 0.906) // #d0e0e7 (azul claro Daycoval)
      : destino === 'abc'
      ? rgb(0.894, 0.859, 0.788) // #e4dbc9 (cor secundária ABC)
      : destino === 'galapagos'
      ? rgb(0.729, 0.769, 0.851) // #bac4d9 (cor secundária Galapagos)
      : destino === 'genial'
      ? rgb(0.702, 0.722, 0.871) // #b3b8de
      : destino === 'safra'
      ? rgb(0.702, 0.702, 0.769) // #b3b3c4
      : destino === 'ezze'
      ? rgb(0.851, 0.941, 0.816) // verde claro Ezze (#2DC52E)
      : destino === 'lockton'
      ? rgb(0.725, 0.725, 0.725) // #B9B9B9
      : destino === 'swissre'
      ? rgb(0.384, 0.490, 0.467) // #627D77
      : destino === 'aon'
      ? rgb(0.918, 0, 0.039) // #EA000A
      : destino === 'avla'
      ? rgb(0.702, 0.831, 0.941) // #B3D4F0
      : destino === 'tokio'
      ? rgb(0.722, 0.792, 0.733) // #B8CABB
      : rgb(0.984, 0.820, 0.741) // #fbd1bd (laranja claro Pottencial)
    const apolicesBoxHeight = 50
    const apolicesBoxY = yPosition - apolicesBoxHeight
    
    // Desenhar retângulo de fundo
    page.drawRectangle({
      x: containerX,
      y: apolicesBoxY,
      width: containerWidth,
      height: apolicesBoxHeight,
      color: apolicesBgColor,
    })
    
    // Desenhar bordas arredondadas com a mesma cor do fundo para criar o efeito
    drawRoundedRect(
      page,
      containerX,
      apolicesBoxY,
      containerWidth,
      apolicesBoxHeight,
      8, // Raio de 8px
      apolicesBgColor,
      6 // Espessura maior da borda para cobrir os cantos completamente
    )
    
    // Ícone de documento alternativo (sem quadrado de fundo)
    const apolicesIconSize = 30
    const apolicesIconX = containerX + 15
    const apolicesIconY = apolicesBoxY + (apolicesBoxHeight - apolicesIconSize) / 2
    
    if (paperAltIcon) {
      page.drawImage(paperAltIcon, {
        x: apolicesIconX,
        y: apolicesIconY,
        width: apolicesIconSize + 5,
        height: apolicesIconSize,
      })
    }
    
    // Texto "Apólice no Processo:"
    const apolicesTextX = apolicesIconX + apolicesIconSize + 15
    const apolicesTextY = apolicesBoxY + (apolicesBoxHeight / 2) - 3
    
    page.drawText('Apólice no Processo:', {
      x: apolicesTextX,
      y: apolicesTextY,
      size: 12,
      font: fontBold,
      color: rgb(0, 0, 0),
    })
    
    // Atualizar yPosition após o título
    yPosition = apolicesBoxY - 10
    
    // Seção Apólices no Processo - sempre exibir (vazio se não houver dados)
    const apolices = Array.isArray(processo?.Apolices) ? processo.Apolices : []
    const apolicesStartY = yPosition
    
    // Altura fixa de 150px para o card de apólices
    const rowHeight = 20
    const tableHeaderHeight = 25
    const numRows = apolices.length > 0 ? Math.min(apolices.length, 5) : 1 // Mínimo 1 linha vazia
    const spacingToFooter = 10 // Espaçamento de 10px até o footer
    const apolicesHeight = 150 // Altura fixa de 150px
    const apolicesEndY = apolicesStartY - apolicesHeight
    
    // Garantir que não entre no footer (10px de margem)
    const minEndY = footerHeight + spacingToFooter
    const finalApolicesEndY = Math.max(apolicesEndY, minEndY)
    
    yPosition -= 10
    
    // Tabela de Apólices - Layout simplificado seguindo o padrão da foto
    const tableStartY = yPosition
    const tableX = containerX
    const tableWidth = containerWidth
    
    // Definir posições X e larguras para cada coluna (ajustadas para margem de 20px)
    const colPositions = [
      tableX + 15,           // Seguradora
      tableX + 120,          // Corretora  
      tableX + 225,          // Importância Segurada
      tableX + 330,          // Prêmio
      tableX + 415,          // Taxa
      tableX + 460,          // Início da Vigência
      tableX + 515,          // Fim da Vigência
    ]
    
    // Larguras disponíveis para cada coluna (diferença entre posições, sem desconto para usar toda a largura)
    const colWidths = [
      colPositions[1] - colPositions[0],  // Seguradora
      colPositions[2] - colPositions[1],  // Corretora
      colPositions[3] - colPositions[2],  // Importância Segurada
      colPositions[4] - colPositions[3],  // Prêmio
      colPositions[5] - colPositions[4],  // Taxa
      colPositions[6] - colPositions[5], // Início da Vigência
      (tableX + tableWidth) - colPositions[6], // Fim da Vigência (até o final da tabela)
    ]
    
    // Função auxiliar para quebrar texto em células - quebra apenas quando atinge o limite exato
    const wrapTextInCell = (text, maxWidth, font, fontSize) => {
      // Sanitizar o texto antes de processar
      const sanitizedText = sanitizeTextForPDF(text)
      const words = sanitizedText.split(' ')
      const lines = []
      let currentLine = ''
      
      for (const word of words) {
        // Testar se a palavra sozinha cabe na linha atual
        const wordWidth = font.widthOfTextAtSize(word, fontSize)
        
        // Se a palavra sozinha já ultrapassa o limite, quebrar por caracteres
        if (wordWidth > maxWidth) {
          // Adicionar a linha atual se houver conteúdo
          if (currentLine) {
            lines.push(currentLine)
            currentLine = ''
          }
          
          // Quebrar palavra longa por caracteres até atingir o limite
          let charLine = ''
          for (const char of word) {
            const testCharLine = charLine + char
            const charWidth = font.widthOfTextAtSize(testCharLine, fontSize)
            
            // Quebrar apenas quando realmente ultrapassar o limite
            if (charWidth > maxWidth && charLine) {
              lines.push(charLine)
              charLine = char
            } else {
              charLine = testCharLine
            }
          }
          
          // Adicionar o restante da palavra quebrada
          if (charLine) {
            currentLine = charLine
          }
        } else {
          // Testar se adicionar a palavra à linha atual ultrapassa o limite
          const testLine = currentLine ? `${currentLine} ${word}` : word
          const testWidth = font.widthOfTextAtSize(testLine, fontSize)
          
          // Quebrar apenas quando realmente ultrapassar o limite exato
          if (testWidth > maxWidth) {
            if (currentLine) {
              lines.push(currentLine)
              currentLine = word
            } else {
              currentLine = word
            }
          } else {
            // Cabe na linha atual, adicionar
            currentLine = testLine
          }
        }
      }
      
      // Adicionar a última linha se houver conteúdo
      if (currentLine) {
        lines.push(currentLine)
      }
      
      return lines.length > 0 ? lines : ['']
    }
    
    // Cabeçalho da tabela (sem fundo)
    const headerRowHeight = 25
    
    // Textos do cabeçalho
    const headers = ['Seguradora', 'Corretora', 'Importância\nSegurada', 'Prêmio', 'Taxa', 'Início da\nVigência', 'Fim da\nVigência']
    const headerFontSize = 8
    
    for (let i = 0; i < headers.length; i++) {
      const headerLines = headers[i].split('\n')
      let yOffset = 0
      
      for (const line of headerLines) {
        // Sanitizar cada linha do cabeçalho
        const sanitizedLine = sanitizeTextForPDF(line)
        page.drawText(sanitizedLine, {
          x: colPositions[i],
          y: tableStartY - headerRowHeight + 13 - yOffset,
          size: headerFontSize,
          font: fontBold,
          color: headerFooterColor, // Texto laranja
        })
        yOffset += 10
      }
    }
    
    // Linha horizontal abaixo dos títulos (border-bottom)
    page.drawLine({
      start: { x: tableX, y: tableStartY - headerRowHeight },
      end: { x: tableX + tableWidth, y: tableStartY - headerRowHeight },
      thickness: 1,
      color: headerFooterColor,
    })
    
    // Linhas de dados
    let dataY = tableStartY - headerRowHeight
    const baseRowHeight = 22
    const lineSpacing = 7 // Espaçamento entre linhas dentro da célula
    const cellPaddingTop = 6 // Padding do topo da célula
    const cellPaddingBottom = 4 // Padding da base da célula (garantir espaço acima da linha)
    const textMarginTop = 5 // Margin-top adicional para os textos
    const dataFontSize = 7.5
    const corretoraFontSize = 6.5 // Tamanho de fonte menor para a coluna Corretora
    const corretoraColIndex = 1 // Índice da coluna Corretora
    
    if (apolices.length > 0) {
      // Desenhar dados das apólices
      for (let rowIdx = 0; rowIdx < numRows; rowIdx++) {
        const apolice = apolices[rowIdx]
        
        // Formatar dados e sanitizar
        const seguradora = sanitizeTextForPDF(String(apolice.Seguradora || 'Swiss Re Corporate Solutions Brasil Seguros S.A.'))
        const corretora = sanitizeTextForPDF(String(apolice.Corretora || 'ITSSEG CORRETORADEESEGUROSS.A.'))
        const importancia = apolice.ImportanciaSegurada 
          ? sanitizeTextForPDF(parseFloat(apolice.ImportanciaSegurada).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2}))
          : '564.913.741,47'
        const premio = apolice.Premio 
          ? sanitizeTextForPDF(`R$ ${parseFloat(apolice.Premio).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`)
          : 'R$ 23.758.879,03'
        const taxa = apolice.Taxa 
          ? sanitizeTextForPDF(`${parseFloat(apolice.Taxa).toFixed(2).replace('.', ',')}%`)
          : '2,10%'
        const inicioVig = sanitizeTextForPDF(apolice.InicioVigencia || '15/02/2024')
        const fimVig = sanitizeTextForPDF(apolice.FimVigencia || '15/02/2026')
        
        const dataRow = [seguradora, corretora, importancia, premio, taxa, inicioVig, fimVig]
        
        // Quebrar texto de cada coluna e calcular altura necessária
        const cellLines = []
        const cellFontSizes = [] // Armazenar tamanho de fonte de cada coluna
        let maxLines = 1
        
        for (let i = 0; i < dataRow.length; i++) {
          // Usar tamanho de fonte menor para a coluna Corretora
          const fontSize = i === corretoraColIndex ? corretoraFontSize : dataFontSize
          const lines = wrapTextInCell(dataRow[i], colWidths[i], fontRegular, fontSize)
          cellLines.push(lines)
          cellFontSizes.push(fontSize)
          maxLines = Math.max(maxLines, lines.length)
        }
        
        // Calcular altura dinâmica da linha baseada no número máximo de linhas
        // Usar o maior tamanho de fonte para calcular a altura (para garantir que tudo caiba)
        const maxFontSize = Math.max(...cellFontSizes)
        const firstLineHeight = maxFontSize
        const additionalLinesHeight = maxLines > 1 ? (maxLines - 1) * (maxFontSize + lineSpacing) : 0
        const textHeight = firstLineHeight + additionalLinesHeight
        const dynamicRowHeight = cellPaddingTop + textMarginTop + textHeight + cellPaddingBottom
        
        // Guardar o topo da linha atual (antes de mover dataY)
        const rowTopY = dataY
        
        // Mover dataY para a base da linha (onde será desenhada a linha separadora)
        dataY -= dynamicRowHeight
        
        // Desenhar texto de cada coluna com quebra de linha
        for (let i = 0; i < cellLines.length; i++) {
          const lines = cellLines[i]
          const fontSize = cellFontSizes[i]
          // Posição Y inicial: topo da linha - padding top - margin top
          // Ajustar para alinhar verticalmente quando a fonte for menor
          const verticalOffset = (maxFontSize - fontSize) / 2
          let lineY = rowTopY - cellPaddingTop - textMarginTop + verticalOffset
          
          for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
            page.drawText(lines[lineIdx], {
              x: colPositions[i],
              y: lineY,
              size: fontSize,
              font: fontRegular,
              color: textColor,
            })
            // Mover para a próxima linha (subtrair altura da fonte + espaçamento)
            lineY -= (fontSize + lineSpacing)
          }
        }
        
        // Linha horizontal laranja separando as rows (desenhar na base da linha atual)
        page.drawLine({
          start: { x: tableX, y: dataY },
          end: { x: tableX + tableWidth, y: dataY },
          thickness: 1,
          color: headerFooterColor,
        })
      }
    } else {
      // Sem apólices - criar uma linha vazia
      dataY -= baseRowHeight
      
      // Linha horizontal
      page.drawLine({
        start: { x: tableX, y: dataY },
        end: { x: tableX + tableWidth, y: dataY },
        thickness: 1,
        color: headerFooterColor,
      })
    }
    
    // Bordas externas da tabela (apenas superior e inferior)
    // Borda superior
    page.drawLine({
      start: { x: tableX, y: tableStartY },
      end: { x: tableX + tableWidth, y: tableStartY },
      thickness: 1,
      color: headerFooterColor,
    })
    
    // Borda inferior
    page.drawLine({
      start: { x: tableX, y: dataY },
      end: { x: tableX + tableWidth, y: dataY },
      thickness: 1,
      color: headerFooterColor,
    })
    
    // Salvar o PDF
    const pdfBytes = await pdfDoc.save()
    
    // Converter para Blob
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    
    return blob
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    throw error
  }
}

/**
 * Sanitiza texto removendo caracteres que não podem ser codificados em WinAnsi
 * Substitui quebras de linha por espaços e remove outros caracteres problemáticos
 */
function sanitizeTextForPDF(text) {
  if (!text) return ''
  return String(text)
    .replace(/\r\n/g, ' ')  // Substituir \r\n por espaço
    .replace(/\n/g, ' ')     // Substituir \n por espaço
    .replace(/\r/g, ' ')     // Substituir \r por espaço
    .replace(/\t/g, ' ')     // Substituir tab por espaço
    .replace(/\s+/g, ' ')    // Múltiplos espaços por um único espaço
    .trim()
}

/**
 * Função auxiliar para quebrar texto em linhas baseado na largura real
 */
function wrapText(text, maxWidth, font, fontSize) {
  // Sanitizar o texto antes de processar
  const sanitizedText = sanitizeTextForPDF(text)
  const words = sanitizedText.split(' ')
  const lines = []
  let currentLine = ''
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const testWidth = font.widthOfTextAtSize(testLine, fontSize)
    
    if (testWidth > maxWidth) {
      if (currentLine) {
        lines.push(currentLine)
        currentLine = word
      } else {
        // Palavra muito longa, adicionar mesmo assim
        lines.push(word)
        currentLine = ''
      }
    } else {
      currentLine = testLine
    }
  }
  
  if (currentLine) {
    lines.push(currentLine)
  }
  
  return lines
}

/**
 * Baixa o PDF gerado
 */
export function downloadPDF(blob, filename = 'processo.pdf') {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.URL.revokeObjectURL(url)
}
